import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Sparkles, Zap, TrendingUp, ShieldAlert, Loader2, Plus, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { fetchJson } from '@/lib/fetchJson';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AIUsagePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestOpen, setRequestOpen] = useState(false);
  const [reqLimit, setReqLimit] = useState('');
  const [reqReason, setReqReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  useEffect(() => {
    (async () => {
      try { setData(await fetchJson(`${API}/api/ai/usage/me`)); }
      catch (e) { toast.error(e.message || 'Could not load AI usage'); }
      finally { setLoading(false); }
    })();
  }, []);

  const chartData = useMemo(() => {
    return (data?.monthly || []).slice().reverse().map(m => ({
      month: m.month, calls: m.total_calls || 0, tokens: m.total_tokens || 0, cost: m.estimated_cost || 0,
    }));
  }, [data]);

  const endpointRows = useMemo(() => {
    const m = data?.by_endpoint || {};
    return Object.entries(m).map(([k, v]) => ({ endpoint: k, ...v })).sort((a, b) => b.tokens - a.tokens);
  }, [data]);

  const credits = data?.credits || {};
  const pct = credits.limit && credits.used != null ? Math.min(100, (credits.used / credits.limit) * 100) : null;
  const status = data?.status || 'ok';
  const bandColor = status === 'critical' ? 'text-red-400' : status === 'warning' ? 'text-amber-400' : 'text-emerald-400';
  const bandBg = status === 'critical' ? 'bg-red-500/5 border-red-500/30' : status === 'warning' ? 'bg-amber-500/5 border-amber-500/30' : 'bg-emerald-500/5 border-emerald-500/30';

  const submitCreditRequest = async () => {
    setSubmitting(true);
    try {
      const out = await fetchJson(`${API}/api/ai/usage/request-credits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requested_limit: Number(reqLimit) || 0, reason: reqReason }),
      });
      setConfirmId(out.request_id);
      setRequestOpen(false);
      setReqLimit(''); setReqReason('');
    } catch (e) { toast.error(e.message || 'Could not send request'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="p-8 flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="ai-usage-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />AI Spend</h1>
          <p className="text-xs text-muted-foreground">Monthly AI usage across your tenant, with soft-cap warnings at 80% and 95%.</p>
        </div>
        <Button onClick={() => setRequestOpen(true)} data-testid="buy-credits-btn" className="rounded-xl bg-primary text-primary-foreground shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all active:scale-95">
          <Plus className="w-4 h-4 mr-2" /> Buy more credits
        </Button>
      </div>

      {/* Credits / cap banner */}
      <Card className={`p-4 rounded-2xl border ${bandBg}`} data-testid="credits-banner">
        <div className="flex items-start gap-3">
          <ShieldAlert className={`w-5 h-5 ${bandColor} flex-shrink-0 mt-0.5`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <p className={`text-sm font-heading font-semibold ${bandColor}`}>
                {status === 'critical' && 'Over 95% of this month'}
                {status === 'warning' && 'Over 80% of this month'}
                {status === 'ok' && (credits.limit ? 'Healthy usage' : 'No monthly cap configured')}
              </p>
              {credits.limit != null && <Badge variant="outline" className="text-[10px]">{credits.used || 0} / {credits.limit} calls</Badge>}
            </div>
            {pct != null && <Progress value={pct} className="h-1.5" />}
            <p className="text-[10px] text-muted-foreground mt-1.5">
              {status === 'critical' && 'You are about to exhaust this month\'s AI budget. Buy more credits to avoid throttling.'}
              {status === 'warning' && 'Usage is rising fast. Consider increasing your monthly cap.'}
              {status === 'ok' && credits.limit && 'Usage is within expected range.'}
              {!credits.limit && 'Ask your admin to set a monthly limit to enable soft-cap warnings.'}
            </p>
          </div>
        </div>
      </Card>

      {/* Monthly chart */}
      <Card className="p-4 rounded-2xl border border-border/50 glass-card" data-testid="monthly-chart">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-heading font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" />Calls per month</p>
        </div>
        {chartData.length ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} />
              <Tooltip contentStyle={{ background: 'var(--glass-bg)', border: '1px solid var(--border)', borderRadius: 8 }} />
              <Bar dataKey="calls" fill="#3b82f6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="p-6 text-center text-xs text-muted-foreground">No usage yet.</div>}
      </Card>

      {/* By endpoint */}
      <Card className="p-4 rounded-2xl border border-border/50 glass-card" data-testid="by-endpoint">
        <p className="text-sm font-heading font-semibold mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-primary" />By endpoint (recent)</p>
        <div className="divide-y divide-border/30">
          {endpointRows.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">No recent calls</div>}
          {endpointRows.map(r => (
            <div key={r.endpoint} className="py-2 flex items-center gap-3 text-xs">
              <code className="text-[10px] px-2 py-0.5 bg-muted/40 border border-border/30 rounded font-mono">{r.endpoint}</code>
              <div className="flex-1" />
              <div className="tabular-nums text-muted-foreground w-16 text-right">{r.calls} calls</div>
              <div className="tabular-nums text-muted-foreground w-24 text-right">{r.tokens.toLocaleString()} tok</div>
              <div className="tabular-nums text-foreground w-20 text-right">${r.cost.toFixed(4)}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Buy credits dialog */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Buy more AI credits</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-muted-foreground">Tell us how much you need for the month. Your request goes to your admin and the Emergent billing team.</p>
            <Input type="number" value={reqLimit} onChange={e => setReqLimit(e.target.value)} placeholder="New monthly limit (calls)" data-testid="req-limit-input" className="rounded-lg" />
            <Textarea value={reqReason} onChange={e => setReqReason(e.target.value)} placeholder="Why do you need more? (optional)" rows={3} data-testid="req-reason-input" className="rounded-lg text-xs" />
            <Button onClick={submitCreditRequest} disabled={submitting || !reqLimit} data-testid="submit-credits-btn" className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Send request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation modal */}
      <Dialog open={!!confirmId} onOpenChange={v => { if (!v) setConfirmId(null); }}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm" data-testid="credits-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" />Request received</DialogTitle></DialogHeader>
          <div className="mt-2 text-xs text-muted-foreground">We'll get back to you within 1 business day. Reference: <code className="text-foreground">{confirmId}</code></div>
          <Button onClick={() => setConfirmId(null)} className="w-full mt-3 rounded-lg">Close</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
